/**
 * Invoice checkout screen
 *
 * Route: `/webAppOnboarding/insurance-invoice-checkout`. Consent, legal docs, checkout table,
 * and course payment callables for the GKV invoice flow.
 *
 * Shared logic/presentation live in `useInsuranceCheckoutScreen` + `InsuranceCheckoutLayout`;
 * this screen only supplies the variant and its billing-detail drawer.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard` (callables use signed-in session).
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('invoice')`; `useSaveOnboardingStep('insurance_invoice_checkout')`.
 */

import { InsuranceCheckoutLayout } from '../components/content/InsuranceCheckoutLayout';
import { InsuranceInvoiceBillingDetailDrawer } from '../components/content/InsuranceInvoiceBillingDetailDrawer';
import { useInsuranceCheckoutScreen } from '../hooks/useInsuranceCheckoutScreen';
import { isPatientSubmittingInsurer } from '../insurance/metaBuilders';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

export function WebOnboardingInsuranceInvoiceCheckoutScreen() {
  const checkout = useInsuranceCheckoutScreen('invoice');
  const { insurance, insuranceShortName, billingMath, isBillingDetailOpen, setIsBillingDetailOpen } = checkout;

  const billingDetailDrawer =
    billingMath && insuranceShortName ? (
      <InsuranceInvoiceBillingDetailDrawer
        open={isBillingDetailOpen}
        onClose={() => setIsBillingDetailOpen(false)}
        insuranceShortName={insuranceShortName}
        courseAmount={billingMath.courseAmount}
        refundRate={billingMath.refundRate}
        reimbursement={billingMath.reimbursement}
        hasBonus={Boolean(insurance?.bonus?.hasBonus)}
        patientSubmits={insurance ? isPatientSubmittingInsurer(insurance) : false}
      />
    ) : null;

  return <InsuranceCheckoutLayout checkout={checkout} billingDetailDrawer={billingDetailDrawer} />;
}
